import { ReactComponent as InforIcon } from "../../shared/assets/informationLogo.svg";
import { themeAtom } from "../../shared/atoms";
import Input from "../../shared/components/Input";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { TechFormSectionLoadError } from "../components/TechFormSectionLoadError";
import type { TechFormPageProps } from "../models/tech-form-page.models";
import { TECH_FORM_ENDPOINTS } from "../models/tech-form-section-data.constants";
import { TECH_FORM_CACHE_MODES } from "../models/tech-form-vessel-cache.constants";
import { Tq6SecaM, mapSqlToTq6SecaForm, tq6SecaFormInit } from "../models/tq6-seca.models";
import { isTechFormSectionDataCached, loadTechFormEndpointData } from "../services/tech-form-section-data.service";
import { tq6SecaAtom } from "../state/tech-form.atoms";
import { sanitizeTechFormInputForField, validateTq6Seca } from "../utils/tech-form-validations";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";

export const Tq6Seca: React.FC<TechFormPageProps> = ({ isCompletedForm, cacheInitialForm, onSectionReady }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(tq6SecaAtom);

  const [form, setForm] = useState<Tq6SecaM>(tq6SecaFormInit);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof Tq6SecaM, string>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSectionDataReady, setIsSectionDataReady] = useState(false);
  const [sectionLoadError, setSectionLoadError] = useState(false);
  const [sectionLoadAttempt, setSectionLoadAttempt] = useState(0);
  const { imoNumber, vesselName } = useParams();

  useEffect(() => {
    let ignoreResult = false;
    setSectionLoadError(false);
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      void fetchData(() => ignoreResult);
    } else {
      if (storageisEmpty) {
        void fetchData(() => ignoreResult);
      } else {
        setForm(_form);
        cacheInitialForm(_form);
        setIsSectionDataReady(true);
        onSectionReady?.();
      }
    }

    return () => {
      ignoreResult = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm, imoNumber, sectionLoadAttempt, vesselName]);

  useEffect(() => {
    setFormErrors(validateTq6Seca(form));
  }, [form]);

  const fetchData = async (isStale: () => boolean) => {
    try {
      if (imoNumber && vesselName) {
        const requestIdentity = {
          imoNumber,
          vesselName,
          mode: isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable,
        };
        if (!isTechFormSectionDataCached({ ...requestIdentity, section: 6 })) {
          setIsLoading(true);
        }
        const result = await loadTechFormEndpointData<any[]>({
          ...requestIdentity,
          endpoint: TECH_FORM_ENDPOINTS.seca,
        });
        if (isStale()) return;

        const _seca = result[0];
        const seca = _seca ? mapSqlToTq6SecaForm(_seca) : { ...tq6SecaFormInit };
        setForm(seca);
        setFormErrors(validateTq6Seca(seca));
        setStoredForm(seca);
        cacheInitialForm(seca);
        setIsSectionDataReady(true);
        onSectionReady?.();
      }
    } catch (error: unknown) {
      if (!isStale()) {
        setSectionLoadError(true);
        console.error("Unable to load Technical Form SECA data", error);
      }
    } finally {
      if (!isStale()) {
        setIsLoading(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeTechFormInputForField(name, value);

    setForm((prevState) => {
      const nextForm = {
        ...prevState,
        [name]: sanitizedValue,
      };
      setFormErrors(validateTq6Seca(nextForm));
      return nextForm;
    });

    setStoredForm({
      ...form,
      [name]: sanitizedValue,
    });
  };

  // Storage
  useEffect(() => {
    if (isSectionDataReady) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm, form, isSectionDataReady]);

  if (sectionLoadError) {
    return <TechFormSectionLoadError onRetry={() => setSectionLoadAttempt((attempt) => attempt + 1)} />;
  }

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
        )}
      >
        <h1 className="custom-heading">SECA</h1>
        <label className="mb-4">
          In case vessel might enter a SECA or other zone in which fuel quality is restricted,
          <br /> please provide the following additional information regarding your fuel tanks:
        </label>
        <br />

        <table className="w-50 mt-6 border-separate border-spacing-x-4 text-left text-sm">
          <thead className="text-sm font-medium">
            <tr>
              <th className="pl-0 pr-1">
                <label className="one-line-label">
                  Capacity of the LSFO
                  <br /> mixing / dearation tank
                  <span>
                    <InforIcon
                      className="h-3 w-3"
                      data-tooltip-id="tooltip-lsfo"
                      data-tooltip-html="The tank collecting return-fuel<br /> from the main enginebefore mixing with new fuel<br /> for resupply back to the main engine"
                    />
                  </span>
                  <Tooltip
                    id="tooltip-lsfo"
                    disableStyleInjection={true}
                    style={{
                      color: "#fff",
                      background: "#4A5655",
                      borderRadius: "8px",
                      position: "absolute",
                      right: "0",
                    }}
                  />
                </label>
              </th>
              <th className="pl-0 pr-1">
                <label className="block text-sm font-medium">
                  Unpumpable quantity/
                  <br />
                  capacity (if any) of the
                  <br /> LSFO settling tank
                </label>
              </th>
              <th className="pl-0 pr-1">
                <label className="one-line-label">
                  Minimal feasible level of
                  <br /> LSFO service tank
                  <span>
                    {" "}
                    <InforIcon
                      className="h-3 w-3"
                      data-tooltip-id="tooltip-lastDry"
                      data-tooltip-html="Starting at its minimum feasible level<br /> and refiling to reach the minimum feasible level"
                    />
                  </span>
                  <Tooltip
                    id="tooltip-lastDry"
                    disableStyleInjection={true}
                    style={{
                      color: "#fff",
                      background: "#4A5655",
                      borderRadius: "8px",
                      position: "absolute",
                      right: "0",
                    }}
                  />
                </label>
              </th>
              <th className="pl-0 pr-1">
                <label className="block text-sm font-medium">
                  Minimal feasible time for
                  <br /> completely draining the
                  <br /> service tank
                </label>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <Input
                  type="text"
                  name="capacity"
                  value={form.capacity}
                  onChange={handleInputChange}
                  suffix="m³"
                  error={formErrors.capacity}
                  placeholder="Enter capacity.."
                />
              </td>
              <td>
                <Input
                  type="text"
                  name="umpumpable"
                  value={form.umpumpable}
                  onChange={handleInputChange}
                  suffix="m³"
                  error={formErrors.umpumpable}
                  placeholder="Enter quantity.."
                />
              </td>
              <td>
                <Input
                  type="text"
                  name="lsfoservicetank"
                  value={form.lsfoservicetank}
                  onChange={handleInputChange}
                  suffix="%"
                  error={formErrors.lsfoservicetank}
                  placeholder="Enter level.."
                />
              </td>
              <td>
                <Input
                  type="text"
                  name="minimalfeasble"
                  value={form.minimalfeasble}
                  onChange={handleInputChange}
                  suffix="h"
                  error={formErrors.minimalfeasble}
                  placeholder="Enter time.."
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </LoadingWrapper>
  );
};
